<script>
export default {
  props: {
    to: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelColor: {
      type: String,
      default: '',
    },
    shouldTruncate: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    warningIcon: {
      type: String,
      default: '',
    },
    showChildCount: {
      type: Boolean,
      default: false,
    },
    childItemCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    showIcon() {
      return {
        'overflow-hidden whitespace-nowrap text-ellipsis': this.shouldTruncate,
      };
    },
    isCountZero() {
      return this.childItemCount === 0;
    },
    menuTitle() {
      return this.shouldTruncate ? this.label : '';
    },
  },
};
</script>

<template>
  <router-link
    v-slot="{ href, isActive, navigate }"
    :to="to"
    custom
    active-class="active"
  >
    <li
      class="sidebar-child-item font-medium h-7 my-1 flex items-center px-2 rounded-full"
      :class="{
        'sidebar-child-item--active': isActive,
        'text-ellipsis overflow-hidden whitespace-nowrap max-w-full':
          shouldTruncate,
      }"
      @click="navigate"
    >
      <a
        :href="href"
        class="inline-flex text-left max-w-full w-full items-center"
      >
        <span
          v-if="icon"
          class="inline-flex items-center justify-center w-4 rounded-sm bg-slate-100 dark:bg-slate-700 p-0.5 mr-1.5 rtl:mr-0 rtl:ml-1.5"
        >
          <fluent-icon
            class="text-xxs text-slate-700 dark:text-slate-200"
            :class="{ 'sidebar-child-item__icon--active': isActive }"
            :icon="icon"
            size="12"
          />
        </span>

        <span
          v-if="labelColor"
          class="inline-flex rounded-sm bg-slate-100 h-3 w-3.5 mr-1.5 rtl:mr-0 rtl:ml-1.5 border border-slate-50 dark:border-slate-900"
          :style="{ backgroundColor: labelColor }"
        />
        <div
          class="items-center flex overflow-hidden whitespace-nowrap text-ellipsis w-full justify-between"
        >
          <span
            :title="menuTitle"
            class="sidebar-child-item__label text-sm"
            :class="{
              'sidebar-child-item__label--active': isActive,
              'text-ellipsis overflow-hidden whitespace-nowrap max-w-full':
                shouldTruncate,
            }"
          >
            {{ label }}
          </span>
          <span
            v-if="showChildCount"
            class="rounded-full min-w-[18px] justify-center items-center flex text-xxs font-medium mx-1 py-0 px-1"
            :class="{
              'sidebar-child-item__count--active': isActive,
              'bg-slate-50 dark:bg-slate-700': !isActive,
              'text-slate-300 dark:text-slate-500': isCountZero && !isActive,
              'text-slate-700 dark:text-slate-50': !isCountZero && !isActive,
            }"
          >
            {{ childItemCount }}
          </span>
        </div>
        <span
          v-if="warningIcon"
          class="inline-flex mr-1 bg-red-50 dark:bg-red-900 p-0.5 rounded-sm"
        >
          <fluent-icon
            v-tooltip.top-end="$t('SIDEBAR.REAUTHORIZE')"
            class="text-xxs text-red-500 dark:text-red-300"
            :icon="warningIcon"
            size="12"
          />
        </span>
      </a>
    </li>
  </router-link>
</template>

<style scoped>
.sidebar-child-item:hover {
  background-color: rgb(var(--color-ash-100));
}

body.dark .sidebar-child-item:hover {
  background-color: rgb(var(--color-ash-75));
}

.sidebar-child-item--active {
  background-color: rgb(var(--color-woot-500)) !important;
  box-shadow: var(--shadow-glow-woot);
}

.sidebar-child-item--active:hover {
  background-color: rgb(var(--color-woot-500)) !important;
}

/*
 * !important on the three rules below: the base/dark-mode text & icon
 * colors (Tailwind's `dark:text-slate-200` and the `body.dark
 * .sidebar-child-item__label` rule) both resolve to a higher specificity
 * than a single custom class, so without it the active state's on-primary
 * color loses and the label/icon read light-on-lime.
 */
.sidebar-child-item__icon--active {
  color: rgb(var(--color-woot-on-primary)) !important;
}

.sidebar-child-item__label {
  color: rgb(var(--color-ash-800));
}

body.dark .sidebar-child-item__label {
  color: rgb(var(--color-ash-900));
}

.sidebar-child-item__label--active {
  color: rgb(var(--color-woot-on-primary)) !important;
}

.sidebar-child-item__count--active {
  background-color: rgb(var(--color-woot-on-primary) / 12%) !important;
  color: rgb(var(--color-woot-on-primary)) !important;
}
</style>
